import "./Header.scss";
import { gsap } from "gsap";
import { define, mount } from "../../../Lib/registry";
import type { Component } from "../../../Lib/types";

interface TextToHeaderEvent extends CustomEvent {
  detail: {
    element: HTMLElement;
    action: "add" | "remove";
  };
}

const DURATION = 0.4;

class Header implements Component {
  readonly el: HTMLElement;
  private readonly infoEl: HTMLElement | null;
  private readonly logoTextEl: HTMLElement | null;
  private readonly infoInner: HTMLElement;
  private readonly headerItems = new WeakMap<HTMLElement, HTMLElement>();

  private readonly logoInitialEl: HTMLElement | null;
  private readonly logoScrolledEl: HTMLElement | null;
  private readonly ctaInitialEl: HTMLElement | null;
  private readonly ctaScrolledEl: HTMLElement | null;
  private isScrolled = false;
  private scrollAbort = new AbortController();

  private readonly gridEl: HTMLElement | null;
  private gridInitialPb = 0;
  private readonly isScrollDisabled: boolean;

  constructor(el: HTMLElement) {
    this.el = el;
    this.isScrollDisabled = el.dataset.scrollDisabled === "1";
    this.infoEl = el.querySelector(".summit-header__info");
    this.logoTextEl = el.querySelector(".summit-header__logo-text");

    this.infoInner = document.createElement("div");
    this.infoInner.className = "summit-header__info-inner";
    this.infoEl?.appendChild(this.infoInner);

    this.logoInitialEl = el.querySelector(".summit-header__logo-initial");
    this.logoScrolledEl = el.querySelector(".summit-header__logo-scrolled");
    this.ctaInitialEl = el.querySelector(".summit-header__cta-initial");
    this.ctaScrolledEl = el.querySelector(".summit-header__cta-scrolled");

    this.gridEl = el.querySelector<HTMLElement>(".summit-header__grid");

    mount(el);
    this.init();
  }

  private init(): void {
    window.addEventListener("textToHeader", this.onTextToHeader);
    if (this.isScrollDisabled) {
      this.initStaticState();
    } else {
      this.initScrollState();
    }
  }

  private initStaticState(): void {
    gsap.set(this.logoScrolledEl, { opacity: 0 });
    gsap.set(this.ctaScrolledEl, { opacity: 0 });
  }

  private initScrollState(): void {
    gsap.set(this.logoScrolledEl, { opacity: 0 });
    gsap.set(this.ctaScrolledEl, { opacity: 0 });

    if (this.gridEl) {
      this.gridInitialPb = parseFloat(
        getComputedStyle(this.gridEl).paddingBottom
      );
    }

    window.addEventListener("scroll", this.onScroll, {
      passive: true,
      signal: this.scrollAbort.signal,
    });
  }

  private onScroll = (): void => {
    const shouldBeScrolled = window.scrollY > 0;
    if (shouldBeScrolled === this.isScrolled) return;

    this.isScrolled = shouldBeScrolled;
    this.transitionToState(shouldBeScrolled);
  };

  private transitionToState(scrolled: boolean): void {
    if (scrolled) {
      this.crossFade(this.logoInitialEl, this.logoScrolledEl);
      this.crossFade(this.ctaInitialEl, this.ctaScrolledEl);
      // this.slideOut(this.ctaInitialEl);
      // this.slideIn(this.ctaScrolledEl);
      gsap.to(this.gridEl, {
        paddingBottom: this.gridInitialPb - 50,
        duration: DURATION,
        ease: "power2.inOut",
      });
    } else {
      this.crossFade(this.logoScrolledEl, this.logoInitialEl);
      this.crossFade(this.ctaScrolledEl, this.ctaInitialEl);
      gsap.to(this.gridEl, {
        paddingBottom: this.gridInitialPb,
        duration: DURATION,
        ease: "power2.inOut",
      });
    }
  }

  private crossFade(outEl: HTMLElement | null, inEl: HTMLElement | null): void {
    if (outEl) {
      gsap.to(outEl, {
        opacity: 0,
        duration: DURATION,
        ease: "power2.inOut",
        onStart: () => {
          outEl.style.pointerEvents = "none";
        },
        onComplete: () => {
          const interactiveElement = outEl.querySelector<
            HTMLAnchorElement | HTMLButtonElement
          >("a, button");
          if (!interactiveElement) return;
          interactiveElement.setAttribute("aria-hidden", "true");
          interactiveElement.setAttribute("tabindex", "-1");
        },
      });
    }

    if (inEl) {
      inEl.removeAttribute("aria-hidden");
      gsap.to(inEl, {
        opacity: 1,
        duration: DURATION,
        ease: "power2.inOut",
        onComplete: () => {
          inEl.style.pointerEvents = "";
          const interactiveElement = inEl.querySelector<
            HTMLAnchorElement | HTMLButtonElement
          >("a, button");
          if (!interactiveElement) return;
          interactiveElement.removeAttribute("aria-hidden");
          interactiveElement.removeAttribute("tabindex");
        },
      });
    }
  }

  private getItems(): HTMLElement[] {
    return Array.from(
      this.infoInner.querySelectorAll<HTMLElement>(".summit-header__info-item")
    );
  }

  private flip(
    items: HTMLElement[],
    firstRects: DOMRect[],
    mutate: () => void
  ): void {
    mutate();

    items.forEach((item, i) => {
      const deltaY = firstRects[i]!.top - item.getBoundingClientRect().top;
      if (deltaY === 0) return;
      gsap.fromTo(
        item,
        { y: deltaY },
        { y: 0, duration: DURATION, ease: "power2.out" }
      );
    });
  }

  private onTextToHeader = (e: Event): void => {
    const { element, action } = (e as TextToHeaderEvent).detail;

    if (!this.infoEl && !this.logoTextEl) return;

    if (action === "add") {
      const existingClone = this.headerItems.get(element);
      if (existingClone && existingClone.parentElement === this.infoInner)
        return;

      existingClone?.remove();

      const existingItems = this.getItems();
      const firstRects = existingItems.map((i) => i.getBoundingClientRect());

      const clone = element.cloneNode(true) as HTMLElement;

      clone.removeAttribute("id");
      clone
        .querySelectorAll("[id]")
        .forEach((node) => node.removeAttribute("id"));
      clone.querySelectorAll("h1, h2").forEach((heading) => {
        const paragraph = document.createElement("p");
        paragraph.innerHTML = heading.innerHTML;

        Array.from(heading.attributes).forEach((attr) => {
          if (attr.name !== "id") {
            paragraph.setAttribute(attr.name, attr.value);
          }
        });

        heading.replaceWith(paragraph);
      });
      clone.classList.add("summit-header__info-item");

      this.flip(existingItems, firstRects, () => {
        this.infoInner.appendChild(clone);
      });

      gsap.fromTo(
        clone,
        { y: "100%", opacity: 0 },
        { y: 0, opacity: 1, duration: DURATION, ease: "power2.out" }
      );

      if (clone.parentElement === this.infoInner) {
        this.headerItems.set(element, clone);
      }
    } else if (action === "remove") {
      const clone = this.headerItems.get(element);
      if (!clone) return;

      const remainingItems = this.getItems().filter((i) => i !== clone);
      const firstRects = remainingItems.map((i) => i.getBoundingClientRect());

      gsap.to(clone, {
        y: "100%",
        opacity: 0,
        duration: DURATION,
        ease: "power2.in",
        onComplete: () => {
          this.flip(remainingItems, firstRects, () => {
            clone.remove();
            this.headerItems.delete(element);
          });
        },
      });
    }
  };

  destroy(): void {
    window.removeEventListener("textToHeader", this.onTextToHeader);
    this.scrollAbort.abort();
  }
}

define("summit-header", (el) => new Header(el));
